import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	type AsyncSelectProps,
	Badge,
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { ListView, TSearchParams } from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo, useState } from "react"
import useSWR from "swr"
import { useAuthUser } from "../Auth"
import { generatePath } from "../router-utils"
import { WalletBalanceCard } from "../SubscriptionWalletCredits/BalanceCard"
import type { TWalletStats } from "../SubscriptionWalletCredits/store"

type TTenantOption = { id: number; name: string }

type TIntervalType = "day" | "week" | "month"

function isIntervalType(value: unknown): value is TIntervalType {
	return value === "day" || value === "week" || value === "month"
}

type TSendStatus = "queued" | "sending" | "sent" | "failed"

type TStats = {
	range: { after: string | null; before: string | null }
	total: number
	by_status: Record<TSendStatus, number>
	total_credits: number
	wallet: TWalletStats | null
}

type TSendRow = {
	id: number
	status: TSendStatus
	error: string | null
	credits: number | null
	sent_at: string | null
	created_at: string
	mail_message: {
		id: number | null
		subject: string | null
		message_id: string | null
		is_orphaned: boolean
	}
	mailbox: { id: number; name: string; email: string } | null
	links: Array<{ type: string; id: number }>
}

type TSendParams = {
	page: number
	after: string
	before: string
	tenant_id?: number
}

const STATUS_LABELS: Record<TSendStatus, string> = {
	queued: "Queued",
	sending: "Sending",
	sent: "Sent",
	failed: "Failed",
}

function SendStatusBadge({ status }: { status: TSendStatus }) {
	if (status === "sent") {
		return <Badge status="success">Sent</Badge>
	}
	if (status === "failed") {
		return <Badge status="danger">Failed</Badge>
	}
	return <Badge status="warning">{STATUS_LABELS[status]}</Badge>
}

const LINK_BUILDERS: Record<string, (id: number, q?: string) => string> = {
	trips: (id, q) => {
		const base = generatePath("/trips/:tripId/emails", { tripId: String(id) })
		return q ? `${base}?q=${encodeURIComponent(q)}` : base
	},
}

/** Deep-link to the owning trip's emails (pre-filtered), else the global search. */
function referenceLink(row: TSendRow): string | null {
	const q = row.mail_message.message_id || row.mail_message.subject || undefined
	for (const link of row.links || []) {
		const build = LINK_BUILDERS[link.type]
		if (build) return build(link.id, q)
	}
	if (q) {
		return `${generatePath("/mail-messages")}?q=${encodeURIComponent(q)}`
	}
	return null
}

function SelectMailSendTenants(props: Omit<AsyncSelectProps, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			cacheKey="mail-send-tenants"
			fetch={(q) =>
				xhr
					.get("/mail-message-sends/tenants", { params: { q } })
					.then((resp) => resp.data.data)
			}
			{...props}
		/>
	)
}

function Stat({ label, value }: { label: string; value: string }) {
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">{label}</Text>
			<Text fontSize="xl" fontWeight="semibold">
				{value}
			</Text>
		</Stack>
	)
}

export function MailSendsUsageView() {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const adminMode = !user?.tenant_id
	const [tenant, setTenant] = useState<TTenantOption | null>(null)
	const [query, setQuery] = useLocationQuery<
		TSearchParams & { interval?: string; anchor?: string },
		TSearchParams & { interval?: string; anchor?: string }
	>()
	const [intervalType, setIntervalType] = useState<TIntervalType>(
		isIntervalType(query.interval) ? query.interval : "month"
	)
	const [cursor, setCursor] = useState<Date>(() =>
		startOf(
			query.anchor
				? parseDateFromQuery(query.anchor, "YYYY-MM-DD")
				: new Date(),
			intervalType
		)
	)
	useEffect(() => {
		setQuery({
			interval: intervalType,
			anchor: dateToQuery(cursor, "YYYY-MM-DD"),
		})
	}, [intervalType, cursor, setQuery])
	const after = startOf(cursor, intervalType)
	const before = endOf(cursor, intervalType)
	const range = useMemo(
		() => ({ after: dateToUTCString(after), before: dateToUTCString(before) }),
		[after, before]
	)
	const tenantId = adminMode && tenant ? tenant.id : undefined

	const [page, setPage] = useState(1)
	useEffect(() => setPage(1), [range.after, range.before, tenantId])
	const params: TSendParams = useMemo(
		() => ({
			page,
			after: range.after,
			before: range.before,
			tenant_id: tenantId,
		}),
		[page, range, tenantId]
	)

	const { data: statsResp } = useSWR<{ data: TStats }>(
		["mail-send-stats", range.after, range.before, tenantId],
		() =>
			xhr
				.get("/mail-message-sends/usage-stats", {
					params: { ...range, tenant_id: tenantId },
				})
				.then((resp) => resp.data)
	)
	const stats = statsResp?.data

	const rangeLabel =
		intervalType === "month"
			? formatDate(after, "MMM YYYY")
			: intervalType === "day"
				? formatDate(after, "DD MMM YYYY")
				: `${formatDate(after, "DD MMM")} – ${formatDate(before, "DD MMM YYYY")}`

	return (
		<Container paddingY="3">
			<Stack gap="4">
				<Inline
					justifyContent="between"
					flexWrap="wrap"
					gap="3"
					alignItems="center"
				>
					<Heading>Email Send</Heading>
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						{adminMode ? (
							<Box style={{ minWidth: "220px" }}>
								<SelectMailSendTenants
									value={tenant || undefined}
									onChange={(value) =>
										setTenant((value as TTenantOption) || null)
									}
									multiple={false}
									placeholder="All tenants"
								/>
							</Box>
						) : null}
						<Button
							onClick={() =>
								setCursor(
									startOf(subtractUnit(cursor, 1, intervalType), intervalType)
								)
							}
							title="Previous"
						>
							<Icons.ChevronDown rotate="90" />
						</Button>
						<Box>
							<SelectInput
								value={intervalType}
								onChange={(e) => {
									const type = (e.currentTarget.value ||
										"month") as TIntervalType
									setIntervalType(type)
									setCursor(startOf(new Date(), type))
								}}
							>
								<option value="month">Month</option>
								<option value="week">Week</option>
								<option value="day">Day</option>
							</SelectInput>
						</Box>
						<Button
							onClick={() =>
								setCursor(
									startOf(addUnit(cursor, 1, intervalType), intervalType)
								)
							}
							title="Next"
						>
							<Icons.ChevronDown rotate="270" />
						</Button>
					</Inline>
				</Inline>

				<Stack
					gap="4"
					bgColor="default"
					padding="4"
					rounded="md"
					borderWidth="1"
				>
					<Text fontWeight="semibold">{rangeLabel}</Text>
					<Inline gap="8" flexWrap="wrap" alignItems="start">
						<WalletBalanceCard
							wallet={stats?.wallet}
							adminTenantId={adminMode && tenant ? tenant.id : undefined}
						/>
						<Stat
							label="Emails Sent"
							value={!stats ? "..." : String(stats.by_status.sent)}
						/>
						<Stat
							label="Failed"
							value={!stats ? "..." : String(stats.by_status.failed)}
						/>
						<Stat
							label="Pending"
							value={
								!stats
									? "..."
									: String(stats.by_status.queued + stats.by_status.sending)
							}
						/>
						<Stat
							label="Credits Used"
							value={!stats ? "..." : stats.total_credits.toFixed(2)}
						/>
					</Inline>
				</Stack>

				<ListView<TSendRow, TSendParams>
					params={params}
					pageKey={`mail-sends${tenantId ? `-t${tenantId}` : ""}`}
					fetch={(api) =>
						api.get("/mail-message-sends", { params }).then((resp) => resp.data)
					}
					onPageChange={(next) => setPage(next)}
					removeContainerPadding
				>
					{({ items }) => (
						<Table
							headers={["Status", "Subject", "Mailbox", "Credits", "When"]}
							bordered
							hover
							responsive
							alignCols={{ 3: "right" }}
							rows={items.map((row) => {
								const subject = row.mail_message.subject || "(No Subject)"
								const link = referenceLink(row)
								return [
									<Stack gap="1">
										<SendStatusBadge status={row.status} />
										{row.status === "failed" && row.error ? (
											<Text fontSize="xs" color="danger">
												{row.error}
											</Text>
										) : null}
									</Stack>,
									link ? (
										<Link to={link} fontWeight="semibold">
											{subject}
										</Link>
									) : (
										<Text fontSize="sm">{subject}</Text>
									),
									<Text fontSize="sm">{row.mailbox?.email || "—"}</Text>,
									<Text fontSize="sm">
										{row.credits != null ? row.credits.toFixed(2) : "—"}
									</Text>,
									<Text fontSize="sm" color="muted">
										<RelativeTime timestamp={row.sent_at || row.created_at} />
									</Text>,
								]
							})}
						/>
					)}
				</ListView>
			</Stack>
		</Container>
	)
}
